import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as explorerService from 'features/Explorer/services/explorerService'
import type {
	ExplorerColumn,
	ExplorerGrain,
	ExplorerGrainKey,
	ExplorerReport,
	SaveAsTemplateInput,
	SaveExplorerReportInput,
} from 'features/Explorer/types/explorer.types'

/**
 * Explorer's saved reports, grains and column catalog (MYD-359).
 *
 * Deliberately NOT here: the rows, and the working definition being edited.
 *
 * Rows are a per-screen, per-page concern that changes on every keystroke in a
 * filter box; putting them in the store would mean every unrelated subscriber
 * re-renders as someone types, and would leave a stale page in the store the
 * moment a report is edited. The screen owns rows in local state, fetched
 * through the service directly.
 *
 * What IS here is the state a user carries between screens: which reports they
 * have, which grains exist, and the catalog for a grain. Those change rarely
 * and are worth not refetching on every tab switch.
 */

const SLICE_NAME = 'explorer'

interface ExplorerSliceState {
	grains: ExplorerGrain[]
	grainsLoaded: boolean
	/** Column catalog per grain. The catalog is server-driven and per-user. */
	columnsByGrain: Record<string, ExplorerColumn[]>
	/** Saved reports per grain. Templates are never in here (MYD-597). */
	reportsByGrain: Record<string, ExplorerReport[]>
	/** Every template the user can start from: theirs and partners' shared ones. */
	templates: ExplorerReport[]
	templatesLoaded: boolean
	isLoading: boolean
	isSaving: boolean
	error: string | null
}

const initialState: ExplorerSliceState = {
	grains: [],
	grainsLoaded: false,
	columnsByGrain: {},
	reportsByGrain: {},
	templates: [],
	templatesLoaded: false,
	isLoading: false,
	isSaving: false,
	error: null,
}

export const fetchGrains = createAsyncThunk(
	`${SLICE_NAME}/fetchGrains`,
	async (_: void, { rejectWithValue }) => {
		try {
			return await explorerService.getGrains()
		} catch {
			return rejectWithValue('Failed to load Explorer')
		}
	},
)

export const fetchColumns = createAsyncThunk(
	`${SLICE_NAME}/fetchColumns`,
	async (grain: ExplorerGrainKey, { rejectWithValue }) => {
		try {
			return { grain, columns: await explorerService.getColumns(grain) }
		} catch {
			return rejectWithValue('Failed to load the column list')
		}
	},
)

export const fetchReports = createAsyncThunk(
	`${SLICE_NAME}/fetchReports`,
	async (grain: ExplorerGrainKey, { rejectWithValue }) => {
		try {
			return { grain, reports: await explorerService.listReports(grain) }
		} catch {
			return rejectWithValue('Failed to load saved reports')
		}
	},
)

export const saveReport = createAsyncThunk(
	`${SLICE_NAME}/saveReport`,
	async (input: SaveExplorerReportInput, { rejectWithValue }) => {
		try {
			return await explorerService.createReport(input)
		} catch (error: any) {
			// The server's message is the useful one here: it names the offending
			// column or explains why a sort is not allowed. A generic string would
			// throw that away and leave the user with nothing to act on.
			return rejectWithValue(error?.response?.data?.message || 'Failed to save the report')
		}
	},
)

export const updateReport = createAsyncThunk(
	`${SLICE_NAME}/updateReport`,
	async (
		{ id, input }: { id: string; input: Partial<SaveExplorerReportInput> },
		{ rejectWithValue },
	) => {
		try {
			return await explorerService.updateReport(id, input)
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to update the report')
		}
	},
)

export const deleteReport = createAsyncThunk(
	`${SLICE_NAME}/deleteReport`,
	async ({ id, grain }: { id: string; grain: ExplorerGrainKey }, { rejectWithValue }) => {
		try {
			await explorerService.deleteReport(id)
			return { id, grain }
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to delete the report')
		}
	},
)

/**
 * Every template at once rather than per tab: the gallery switches tabs
 * locally, and the whole set is small (a user's own plus their partners').
 */
export const fetchTemplates = createAsyncThunk(
	`${SLICE_NAME}/fetchTemplates`,
	async (_: void, { rejectWithValue }) => {
		try {
			return await explorerService.listTemplates()
		} catch {
			return rejectWithValue('Failed to load templates')
		}
	},
)

export const saveReportAsTemplate = createAsyncThunk(
	`${SLICE_NAME}/saveReportAsTemplate`,
	async ({ reportId, input }: { reportId: string; input: SaveAsTemplateInput }, { rejectWithValue }) => {
		try {
			return await explorerService.saveReportAsTemplate(reportId, input)
		} catch (error: any) {
			return rejectWithValue(error?.response?.data?.message || 'Failed to save the template')
		}
	},
)

const byName = (a: ExplorerReport, b: ExplorerReport) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)

/** Insert or replace a report in its grain's list, keeping name order. */
const upsertReport = (state: ExplorerSliceState, report: ExplorerReport) => {
	const list = state.reportsByGrain[report.grain] ?? []
	const index = list.findIndex(existing => existing.id === report.id)
	const next = index >= 0 ? list.map(r => (r.id === report.id ? report : r)) : [...list, report]

	// Names are deliberately non-unique, so an id tiebreak keeps the order
	// stable rather than letting two equal names swap on every save.
	next.sort(byName)
	state.reportsByGrain[report.grain] = next
}

const explorerSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearError: state => {
			state.error = null
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchGrains.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchGrains.fulfilled, (state, action) => {
				state.grains = action.payload as ExplorerGrain[]
				state.grainsLoaded = true
				state.isLoading = false
			})
			.addCase(fetchGrains.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

			.addCase(fetchColumns.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchColumns.fulfilled, (state, action) => {
				const { grain, columns } = action.payload as { grain: string; columns: ExplorerColumn[] }
				state.columnsByGrain[grain] = columns
				state.isLoading = false
			})
			.addCase(fetchColumns.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

			.addCase(fetchReports.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchReports.fulfilled, (state, action) => {
				const { grain, reports } = action.payload as { grain: string; reports: ExplorerReport[] }
				state.reportsByGrain[grain] = reports
				state.isLoading = false
			})
			.addCase(fetchReports.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

			.addCase(saveReport.pending, state => {
				state.isSaving = true
				state.error = null
			})
			.addCase(saveReport.fulfilled, (state, action) => {
				upsertReport(state, action.payload as ExplorerReport)
				state.isSaving = false
			})
			.addCase(saveReport.rejected, (state, action) => {
				state.isSaving = false
				state.error = action.payload as string
			})

			.addCase(updateReport.pending, state => {
				state.isSaving = true
				state.error = null
			})
			.addCase(updateReport.fulfilled, (state, action) => {
				upsertReport(state, action.payload as ExplorerReport)
				state.isSaving = false
			})
			.addCase(updateReport.rejected, (state, action) => {
				state.isSaving = false
				state.error = action.payload as string
			})

			.addCase(deleteReport.fulfilled, (state, action) => {
				const { id, grain } = action.payload as { id: string; grain: string }
				state.reportsByGrain[grain] = (state.reportsByGrain[grain] ?? []).filter(
					report => report.id !== id,
				)
			})
			.addCase(deleteReport.rejected, (state, action) => {
				state.error = action.payload as string
			})

			// Templates load quietly: a failure leaves the gallery's own empty
			// state rather than an error banner over the report grid.
			.addCase(fetchTemplates.fulfilled, (state, action) => {
				state.templates = [...(action.payload as ExplorerReport[])].sort(byName)
				state.templatesLoaded = true
			})
			.addCase(fetchTemplates.rejected, state => {
				state.templatesLoaded = true
			})

			.addCase(saveReportAsTemplate.pending, state => {
				state.isSaving = true
				state.error = null
			})
			.addCase(saveReportAsTemplate.fulfilled, (state, action) => {
				state.templates = [...state.templates, action.payload as ExplorerReport].sort(byName)
				state.isSaving = false
			})
			.addCase(saveReportAsTemplate.rejected, (state, action) => {
				state.isSaving = false
				state.error = action.payload as string
			})
	},
})

export const { clearError } = explorerSlice.actions

export default explorerSlice
